import { useCallback, useEffect, useState } from 'react';
import {
  TbUsers, TbCircleCheck, TbClockHour4, TbAlertTriangle, TbCalendarEvent,
} from 'react-icons/tb';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import './admin.css';

const TITLE = 'Resumen';

function formatCount(value) {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
}

// `value` only reads a field off the server's own DTO — it never derives or
// sums anything. The five status cards follow their dashboard order.
const METRICS = [
  { key: 'membersTotal', label: 'Socios', icon: TbUsers, tone: 'neutral', value: (m) => formatCount(m.membersTotal) },
  { key: 'active', label: 'Al día', icon: TbCircleCheck, tone: 'success', value: (m) => formatCount(m.active) },
  { key: 'pending', label: 'Pendientes', icon: TbClockHour4, tone: 'warning', value: (m) => formatCount(m.pending) },
  { key: 'overdue', label: 'Vencidos', icon: TbAlertTriangle, tone: 'danger', value: (m) => formatCount(m.overdue) },
  { key: 'upcoming', label: 'Próximos', icon: TbCalendarEvent, tone: 'info', value: (m) => formatCount(m.upcoming) },
];

/**
 * `/admin` (spec "Administrador"): five read-only indicators straight off
 * `GET /dashboard`, presented as-is. `api` defaults to the real `adminApi`
 * and is only overridden by tests, same convention as `AuthProvider`'s `api`
 * prop.
 *
 * On failure the error is handed to `handleApiError` first: a 401 or a
 * `password_change_required` clears the session and `RoleRoute` reacts to
 * that by navigating away, so this page only has to stop showing its own
 * (possibly stale) data — it never navigates itself. Any other failure shows
 * the API's own message through the same `PageState` used everywhere else.
 */
export function AdminDashboard({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', metrics: null, message: '' });

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', metrics: null, message: '' });

    api.getDashboard()
      .then((metrics) => {
        if (!active) return;
        setState({ status: 'ready', metrics, message: '' });
      })
      .catch((error) => {
        if (!active) return;
        const clearBusinessData = () => setState({ status: 'loading', metrics: null, message: '' });
        const result = handleApiError(error, { clearBusinessData });
        if (result.handled) return;
        setState({ status: 'error', metrics: null, message: errorMessageFor(error) });
      });

    return () => {
      active = false;
    };
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  if (state.status === 'loading') return <PageState title={TITLE} status="loading" />;
  if (state.status === 'error') return <PageState title={TITLE} status="error" message={state.message} action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>} />;

  const { metrics } = state;
  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">{TITLE}</h1>
      </header>
      <dl className="gym-metrics">
        {METRICS.map(({
          key, label, icon: Icon, tone, value,
        }) => (
          <div key={key} className="gym-metric-card">
            <dt className="gym-metric-card__label">
              <span>{label}</span>
              <Icon className={`gym-metric-card__icon gym-metric-card__icon--${tone}`} aria-hidden="true" focusable="false" />
            </dt>
            <dd className="gym-metric-card__value">{value(metrics)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
